import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, Save, Trash2, Check, Loader2, ExternalLink, Inbox, Reply, Copy, Smartphone, DollarSign } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { useContentAdmin } from "@/context/ContentContext";
import * as staticContent from "@/data/content";
import { Field, LinesField, CsvField, ListEditor } from "@/components/admin/editors";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SECTIONS = [
    { id: "leads", label: "Leads Inbox" },
    { id: "payments", label: "Payment Links" },
    { id: "received", label: "Payments Received" },
    { id: "hero", label: "Hero" },
    { id: "about", label: "About" },
    { id: "contact", label: "Contact Info", type: "fields", key: "contact", fields: [
        { key: "email", label: "Email" }, { key: "location", label: "Location" }, { key: "facebook", label: "Facebook URL" },
        { key: "venmo", label: "Venmo Username (no @)", hint: "Shows a Venmo payment option on the Pricing page and quote confirmation" },
        { key: "depositLink", label: "Deposit Payment Link (Stripe)", hint: "The $1 adjustable-quantity link — shows Pay Your Deposit on the Pricing page" },
        { key: "balanceLink", label: "Final Balance Payment Link (Stripe)", hint: "The $1 adjustable-quantity link for the remaining 60% — shows Pay Your Final Balance on the Pricing page and a text template in Payment Links" },
        { key: "googleReviewLink", label: "Google Review Link", hint: "From your Google Business Profile → 'Ask for reviews' — adds a ready-to-text review request card in Payment Links" },
    ]},
    { id: "mockup", label: "Mockup Offer" },
    { id: "services", label: "Services", type: "list", key: "services", fields: [
        { key: "title", label: "Title" }, { key: "price", label: "Price" },
        { key: "id", label: "ID (lowercase, no spaces)" }, { key: "icon", label: "Icon", hint: "MonitorSmartphone, PenTool, Palette, RefreshCw, LifeBuoy" },
        { key: "tagline", label: "Description", type: "textarea" }, { key: "bullets", label: "Bullet Points", type: "lines" },
    ]},
    { id: "pricing", label: "Pricing", type: "list", key: "pricingPlans", fields: [
        { key: "name", label: "Name" }, { key: "price", label: "Price" },
        { key: "description", label: "Description", type: "textarea" },
        { key: "stripe", label: "Stripe Payment Link", hint: "Leave empty to show Start a Project button" },
        { key: "featured", label: "Featured (Most Popular)", type: "checkbox" },
    ]},
    { id: "testimonials", label: "Testimonials", type: "list", key: "testimonials", fields: [
        { key: "quote", label: "Quote", type: "textarea" }, { key: "name", label: "Name" },
        { key: "sub", label: "Attribution (business, context)" }, { key: "tag", label: "Tag (optional, e.g. Site Redesign)" },
        { key: "layout", label: "Layout", hint: "big, panel, wide, or small" },
    ]},
    { id: "reviews", label: "Review Marquee", type: "list", key: "reviews", fields: [
        { key: "quote", label: "Quote", type: "textarea" }, { key: "name", label: "Name" }, { key: "org", label: "Business / Context" },
    ]},
    { id: "faqs", label: "FAQs", type: "list", key: "faqs", fields: [
        { key: "q", label: "Question" }, { key: "a", label: "Answer", type: "textarea" },
    ]},
    { id: "featured", label: "Featured Project", type: "fields-lines", key: "featuredProject", fields: [
        { key: "name", label: "Name" }, { key: "category", label: "Category" },
        { key: "description", label: "Description", type: "textarea" }, { key: "deliverables", label: "Deliverables", type: "lines" },
    ]},
    { id: "clients", label: "Client Projects", type: "list", key: "clientProjects", fields: [
        { key: "name", label: "Name" }, { key: "slug", label: "Slug (url-friendly)" },
        { key: "category", label: "Category" }, { key: "location", label: "Location" },
        { key: "url", label: "Live Site URL" }, { key: "image", label: "Image Path" },
        { key: "alt", label: "Image Alt Text" }, { key: "blurb", label: "Blurb", type: "textarea" },
    ]},
    { id: "concepts", label: "Concepts", type: "list", key: "conceptProjects", fields: [
        { key: "name", label: "Name" }, { key: "category", label: "Category" },
        { key: "image", label: "Image Path" }, { key: "alt", label: "Image Alt Text" },
    ]},
    { id: "cases", label: "Case Studies", type: "list", key: "caseStudies", fields: [
        { key: "name", label: "Name" }, { key: "slug", label: "Slug (must match /work/ URL)" },
        { key: "category", label: "Category" }, { key: "location", label: "Location" },
        { key: "url", label: "Live Site URL" }, { key: "image", label: "Image Path" },
        { key: "alt", label: "Image Alt Text" }, { key: "headline", label: "Headline" },
        { key: "story", label: "Story (one paragraph per line)", type: "lines" },
        { key: "features", label: "Features Built", type: "lines" },
    ]},
    { id: "industries", label: "Industry Pages", type: "list", key: "industries", fields: [
        { key: "label", label: "Label" }, { key: "slug", label: "Slug (must match /industries/ URL)" },
        { key: "h1", label: "Page Heading" }, { key: "cta", label: "CTA Question" },
        { key: "seoTitle", label: "SEO Title" }, { key: "seoDescription", label: "SEO Description", type: "textarea" },
        { key: "intro", label: "Intro", type: "textarea" },
        { key: "image", label: "Image Path" }, { key: "imageAlt", label: "Image Alt Text" },
        { key: "needs", label: "Checklist Items", type: "lines" },
        { key: "paragraphs", label: "Paragraphs (one per line)", type: "lines" },
        { key: "clients", label: "Case Study Slugs", type: "csv" },
    ]},
    { id: "manifesto", label: "Manifesto", type: "list", key: "manifesto", fields: [
        { key: "n", label: "Number" }, { key: "title", label: "Title" }, { key: "text", label: "Text", type: "textarea" },
    ]},
    { id: "process", label: "Process Steps", type: "list", key: "processSteps", fields: [
        { key: "n", label: "Number" }, { key: "title", label: "Title" }, { key: "text", label: "Text", type: "textarea" },
    ]},
    { id: "marquee", label: "Marquee Strip", type: "lines", key: "marqueeItems" },
    { id: "options", label: "Form Options", type: "options" },
    { id: "settings", label: "Settings" },
];

function LeadComposer({ lead, template, authHeaders, onSent }) {
    const fill = (t) =>
        t.replaceAll("{name}", lead.name)
            .replaceAll("{service}", lead.service)
            .replaceAll("{business}", lead.business_name || "");
    const [subject, setSubject] = useState(`Re: Your ${lead.service} request — Camarca Digital Designs`);
    const [body, setBody] = useState(() => fill(template));
    const [sending, setSending] = useState(false);

    const send = async () => {
        setSending(true);
        try {
            await axios.post(`${API}/quotes/${lead.id}/reply`, { subject, message: body }, { headers: authHeaders() });
            toast.success(`Reply sent to ${lead.email}`);
            onSent(lead.id);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
        } finally {
            setSending(false);
        }
    };

    return (
        <div className="mt-5 space-y-4 border border-zinc-800 bg-void p-5" data-testid={`lead-composer-${lead.id}`}>
            <Field label="Subject" value={subject} onChange={setSubject} testid={`reply-subject-${lead.id}`} />
            <div>
                <label className="mb-1.5 block font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-500">Message</label>
                <textarea
                    rows={8}
                    data-testid={`reply-body-${lead.id}`}
                    className="w-full resize-y rounded-none border border-zinc-800 bg-void px-3.5 py-2.5 text-sm text-white outline-none transition-[border-color] duration-300 focus:border-neon"
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                />
                <p className="mt-1.5 font-mono text-[10px] text-zinc-600">{"Placeholders available in template: {name} {service} {business}"}</p>
            </div>
            <button
                onClick={send}
                disabled={sending || !body.trim()}
                data-testid={`reply-send-${lead.id}`}
                className="flex items-center gap-2 bg-neon px-6 py-3 font-mono text-xs font-medium tracking-[0.2em] uppercase text-void transition-shadow duration-300 hover:shadow-[0_0_20px_rgba(0,240,255,0.4)] disabled:opacity-60"
            >
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send Reply"}
            </button>
        </div>
    );
}

function LeadsInbox({ authHeaders }) {
    const [leads, setLeads] = useState(null);
    const [template, setTemplate] = useState(null);
    const [openReply, setOpenReply] = useState(null);

    const load = async () => {
        try {
            const { data } = await axios.get(`${API}/quotes`, { headers: authHeaders() });
            setLeads(data.quotes);
        } catch (e) {
            toast.error("Could not load leads");
        }
    };
    useEffect(() => {
        load();
        axios.get(`${API}/settings/reply-template`, { headers: authHeaders() })
            .then(({ data }) => setTemplate(data.template))
            .catch(() => setTemplate(""));
    }, []);

    const markRead = async (id) => {
        await axios.patch(`${API}/quotes/${id}/read`, {}, { headers: authHeaders() });
        setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, read: true } : l)));
    };
    const remove = async (id) => {
        await axios.delete(`${API}/quotes/${id}`, { headers: authHeaders() });
        setLeads((ls) => ls.filter((l) => l.id !== id));
        toast.success("Lead deleted");
    };
    const onSent = (id) => {
        setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, read: true, replied_at: new Date().toISOString() } : l)));
        setOpenReply(null);
    };

    if (leads === null) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-neon" /></div>;
    if (leads.length === 0) return <p className="py-20 text-center font-mono text-sm text-zinc-500">No quote requests yet.</p>;

    return (
        <div className="space-y-4" data-testid="leads-list">
            {leads.map((l) => (
                <div key={l.id} data-testid={`lead-card-${l.id}`} className={`border p-6 ${l.read ? "border-zinc-800 bg-surface" : "border-neon/40 bg-surface shadow-[0_0_20px_rgba(0,240,255,0.06)]"}`}>
                    <div className="flex flex-wrap items-start justify-between gap-4">
                        <div>
                            <div className="flex items-center gap-3">
                                {!l.read && <span className="h-2 w-2 rounded-full bg-neon" aria-label="Unread" />}
                                <p className="font-display text-base font-bold">{l.name}</p>
                                <span className="font-mono text-[10px] tracking-widest uppercase text-neon">{l.service}</span>
                                {l.replied_at && <span className="border border-neon/40 px-2 py-0.5 font-mono text-[9px] tracking-widest uppercase text-neon">Replied</span>}
                            </div>
                            <a href={`mailto:${l.email}`} className="mt-1 block font-mono text-xs text-zinc-400 hover:text-neon">{l.email}</a>
                            {l.business_name && <p className="mt-1 font-mono text-xs text-zinc-500">{l.business_name}</p>}
                        </div>
                        <p className="font-mono text-[10px] text-zinc-600">{new Date(l.created_at).toLocaleString()}</p>
                    </div>
                    <div className="mt-4 flex flex-wrap gap-4 font-mono text-[10px] tracking-widest uppercase text-zinc-500">
                        {l.budget && <span>Budget: <span className="text-zinc-300">{l.budget}</span></span>}
                        {l.timeline && <span>Timeline: <span className="text-zinc-300">{l.timeline}</span></span>}
                    </div>
                    {l.message && <p className="mt-4 border-l-2 border-zinc-800 pl-4 text-sm leading-relaxed text-zinc-400">{l.message}</p>}
                    <div className="mt-5 flex flex-wrap gap-3">
                        <button
                            onClick={() => setOpenReply(openReply === l.id ? null : l.id)}
                            disabled={template === null}
                            data-testid={`lead-reply-${l.id}`}
                            className="flex items-center gap-2 border border-neon/60 px-4 py-2 font-mono text-[10px] tracking-[0.2em] uppercase text-neon transition-[background-color,color] duration-300 hover:bg-neon hover:text-void"
                        >
                            <Reply className="h-3.5 w-3.5" /> Reply
                        </button>
                        {!l.read && (
                            <button onClick={() => markRead(l.id)} data-testid={`lead-read-${l.id}`} className="flex items-center gap-2 border border-zinc-700 px-4 py-2 font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-400 transition-colors duration-300 hover:border-neon hover:text-neon">
                                <Check className="h-3.5 w-3.5" /> Mark Read
                            </button>
                        )}
                        <button onClick={() => remove(l.id)} data-testid={`lead-delete-${l.id}`} className="flex items-center gap-2 border border-zinc-700 px-4 py-2 font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-400 transition-colors duration-300 hover:border-red-400 hover:text-red-400">
                            <Trash2 className="h-3.5 w-3.5" /> Delete
                        </button>
                    </div>
                    {openReply === l.id && template !== null && (
                        <LeadComposer lead={l} template={template} authHeaders={authHeaders} onSent={onSent} />
                    )}
                </div>
            ))}
        </div>
    );
}

function Settings({ authHeaders }) {
    const [current, setCurrent] = useState("");
    const [next, setNext] = useState("");
    const [saving, setSaving] = useState(false);
    const [template, setTemplate] = useState(null);
    const [savingTemplate, setSavingTemplate] = useState(false);

    useEffect(() => {
        axios.get(`${API}/settings/reply-template`, { headers: authHeaders() })
            .then(({ data }) => setTemplate(data.template))
            .catch(() => toast.error("Could not load reply template"));
    }, []);

    const saveTemplate = async () => {
        setSavingTemplate(true);
        try {
            await axios.put(`${API}/settings/reply-template`, { template }, { headers: authHeaders() });
            toast.success("Reply template saved");
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
        } finally {
            setSavingTemplate(false);
        }
    };

    const submit = async () => {
        setSaving(true);
        try {
            await axios.post(`${API}/auth/change-password`, { current_password: current, new_password: next }, { headers: authHeaders() });
            toast.success("Password updated");
            setCurrent(""); setNext("");
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };
    return (
        <div className="max-w-xl space-y-8">
            <div className="border border-zinc-800 bg-surface p-6">
                <h3 className="font-display text-base font-bold">Lead Reply Template</h3>
                <p className="mt-2 text-sm leading-relaxed text-zinc-500">
                    Pre-fills the reply composer on every lead. {"Placeholders: {name} {service} {business}"}
                </p>
                {template !== null && (
                    <textarea
                        rows={9}
                        data-testid="settings-reply-template"
                        className="mt-4 w-full resize-y rounded-none border border-zinc-800 bg-void px-3.5 py-2.5 text-sm text-white outline-none transition-[border-color] duration-300 focus:border-neon"
                        value={template}
                        onChange={(e) => setTemplate(e.target.value)}
                    />
                )}
                <button onClick={saveTemplate} disabled={savingTemplate || template === null} data-testid="settings-save-template" className="mt-4 flex items-center gap-2 border border-neon/70 px-5 py-2.5 font-mono text-[10px] tracking-[0.2em] uppercase text-neon transition-[background-color,color] duration-300 hover:bg-neon hover:text-void disabled:opacity-60">
                    {savingTemplate ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Save className="h-3.5 w-3.5" /> Save Template</>}
                </button>
            </div>
            <div className="border border-zinc-800 bg-surface p-6">
                <h3 className="font-display text-base font-bold">Change Password</h3>
                <div className="mt-4 space-y-5">
                    <Field label="Current Password" value={current} onChange={setCurrent} testid="settings-current-password" />
                    <Field label="New Password (min 8 characters)" value={next} onChange={setNext} testid="settings-new-password" />
                    <button onClick={submit} disabled={saving} data-testid="settings-save" className="flex items-center gap-2 bg-neon px-6 py-3 font-mono text-xs font-medium tracking-[0.2em] uppercase text-void transition-shadow duration-300 hover:shadow-[0_0_20px_rgba(0,240,255,0.4)] disabled:opacity-60">
                        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4" /> Update Password</>}
                    </button>
                </div>
            </div>
            <WebhookSettings authHeaders={authHeaders} />
        </div>
    );
}

function PaymentsReceived({ authHeaders }) {
    const [payments, setPayments] = useState(null);

    useEffect(() => {
        axios.get(`${API}/payments`, { headers: authHeaders() })
            .then(({ data }) => setPayments(data.payments))
            .catch(() => {
                toast.error("Could not load payments");
                setPayments([]);
            });
    }, []);

    if (payments === null) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-neon" /></div>;
    if (payments.length === 0) return (
        <div className="py-20 text-center" data-testid="payments-empty">
            <p className="font-mono text-sm text-zinc-500">No payments yet.</p>
            <p className="mt-3 font-mono text-xs leading-relaxed text-zinc-600">
                The moment a client pays any of your Stripe links, it shows up here —<br className="hidden sm:block" />
                and as a push notification on your phone.
            </p>
        </div>
    );

    return (
        <div className="space-y-4" data-testid="payments-list">
            {payments.map((p) => (
                <div key={p.id} data-testid={`payment-row-${p.id}`} className="border border-zinc-800 bg-surface p-5">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                        <div className="flex items-center gap-4">
                            <span className="flex h-10 w-10 shrink-0 items-center justify-center border border-neon/30 text-neon">
                                <DollarSign className="h-4 w-4" />
                            </span>
                            <div>
                                <p className="font-display text-lg font-black">
                                    ${p.amount.toFixed(2)} <span className="font-mono text-[10px] font-normal tracking-widest uppercase text-zinc-500">{p.currency}</span>
                                </p>
                                <p className="mt-0.5 font-mono text-xs text-zinc-400">{p.label}</p>
                            </div>
                        </div>
                        <span className={`border px-2.5 py-1 font-mono text-[9px] tracking-widest uppercase ${
                            p.mode === "subscription" ? "border-neon/40 text-neon" : "border-zinc-700 text-zinc-400"
                        }`}>
                            {p.mode === "subscription" ? "Monthly" : "One-time"}
                        </span>
                    </div>
                    <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-zinc-800/60 pt-3 font-mono text-[10px] tracking-widest text-zinc-500">
                        {p.customer_email ? <span className="text-zinc-400">{p.customer_email}</span> : <span />}
                        <span>{new Date(p.created_at).toLocaleString()}</span>
                    </div>
                </div>
            ))}
        </div>
    );
}

function WebhookSettings({ authHeaders }) {
    const [secret, setSecret] = useState("");
    const [status, setStatus] = useState(null);
    const [saving, setSaving] = useState(false);

    const load = () =>
        axios.get(`${API}/settings/stripe-webhook`, { headers: authHeaders() })
            .then(({ data }) => setStatus(data))
            .catch(() => toast.error("Could not load webhook status"));

    useEffect(() => {
        load();
    }, []);

    const save = async () => {
        setSaving(true);
        try {
            await axios.put(`${API}/settings/stripe-webhook`, { secret }, { headers: authHeaders() });
            toast.success(secret.trim() ? "Payment alerts connected" : "Webhook secret cleared");
            setSecret("");
            await load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="border border-zinc-800 bg-surface p-6" data-testid="webhook-settings">
            <h3 className="font-display text-base font-bold">Payment Alerts (Stripe Webhook)</h3>
            <p className="mt-2 text-sm leading-relaxed text-zinc-500">
                Get a phone push + email the moment a client pays. In your Stripe Dashboard go to Developers → Webhooks → Add endpoint:
            </p>
            <div className="mt-3 space-y-2 border border-zinc-800 bg-void p-4 font-mono text-xs leading-relaxed text-zinc-400">
                <p>Endpoint URL: <span className="select-all break-all text-neon">https://www.camarcadigitaldesigns.com/api/stripe/webhook</span></p>
                <p>Events to select: <span className="text-white">checkout.session.completed</span> and <span className="text-white">invoice.paid</span></p>
                <p className="text-zinc-600">(checkout.session.completed = new payments · invoice.paid = monthly hosting renewal alerts)</p>
                <p className="text-zinc-600">Then paste the Signing secret (starts with whsec_) below.</p>
            </div>
            {status && (
                <p className="mt-3 font-mono text-[10px] tracking-widest uppercase" data-testid="webhook-status">
                    {status.configured
                        ? <span className="text-neon">● Connected — secret ending in {status.secret_tail}{status.source === "env" ? " (server default)" : ""}</span>
                        : <span className="text-amber-400">● Not connected yet</span>}
                </p>
            )}
            <div className="mt-4">
                <Field label="Signing Secret (whsec_...)" value={secret} onChange={setSecret} testid="settings-webhook-secret" />
            </div>
            <button
                onClick={save}
                disabled={saving}
                data-testid="settings-webhook-save"
                className="mt-4 flex items-center gap-2 bg-neon px-6 py-3 font-mono text-xs font-medium tracking-[0.2em] uppercase text-void transition-shadow duration-300 hover:shadow-[0_0_20px_rgba(0,240,255,0.4)] disabled:opacity-60"
            >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4" /> Save & Connect</>}
            </button>
        </div>
    );
}

function PaymentsPanel({ draft }) {
    const copy = async (text, label) => {
        try {
            await navigator.clipboard.writeText(text);
            toast.success(`${label} copied — paste it into any text, Messenger, or DM`);
        } catch (e) {
            toast.error("Copy failed — long-press the link to copy it manually");
        }
    };

    const deposit = draft.contact?.depositLink || "";
    const balance = draft.contact?.balanceLink || "";
    const review = draft.contact?.googleReviewLink || "";
    const plans = (draft.pricingPlans || []).filter((p) => p.stripe);
    const links = [
        ...(deposit
            ? [{
                  name: "Project Deposit",
                  price: "40% of project total",
                  url: deposit,
                  message: `Hi! Nick here from Camarca Digital Designs. Here's your secure link to pay the project deposit and get started: ${deposit} — just set the quantity to match your deposit amount (for example, quantity 60 = $60). Text me once it's through!`,
              }]
            : []),
        ...(balance
            ? [{
                  name: "Final Balance",
                  price: "60% on completion",
                  url: balance,
                  message: `Hi! Nick from Camarca Digital Designs — your website is complete! Here's the link for the remaining balance: ${balance} — set the quantity to match your balance amount. As soon as it's through, your site goes live. Thank you!`,
              }]
            : []),
        ...plans.map((p) => ({
            name: p.name,
            price: p.price,
            url: p.stripe,
            message: p.name.toLowerCase().includes("hosting")
                ? `Hi! Nick from Camarca Digital Designs. Here's the link to set up your $25/month hosting & maintenance: ${p.stripe} — it keeps your website live, secure, and updated. Takes about a minute to set up.`
                : `Hi! Nick from Camarca Digital Designs. Here's your secure payment link for ${p.name} (${p.price}): ${p.stripe} — any questions, just text me back!`,
        })),
        ...(review
            ? [{
                  name: "Google Review Ask",
                  price: "After a happy project",
                  url: review,
                  message: `Hi! Nick from Camarca Digital Designs — so glad you're happy with the work! If you have 60 seconds, a quick Google review would mean the world and helps other local businesses find me: ${review} — thank you!`,
              }]
            : []),
    ];

    if (links.length === 0) {
        return <p className="py-20 text-center font-mono text-sm text-zinc-500">No payment links yet — add Stripe links in Pricing or Contact Info.</p>;
    }

    return (
        <div className="max-w-2xl space-y-6" data-testid="payments-panel">
            <p className="border border-zinc-800 bg-surface p-4 text-sm leading-relaxed text-zinc-400">
                Send a customer a link by text, Messenger, or DM — from any device. On your phone,{" "}
                <span className="text-neon">Text This Link</span> opens your messaging app with a ready-to-send message. Edit links
                anytime in Pricing or Contact Info.
            </p>
            {links.map((l) => {
                const slug = l.name.toLowerCase().replace(/[^a-z0-9]+/g, "-");
                return (
                    <div key={l.name} data-testid={`paylink-card-${slug}`} className="border border-zinc-800 bg-surface p-6">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                            <p className="font-display text-base font-bold">{l.name}</p>
                            <span className="font-mono text-[10px] tracking-widest uppercase text-neon">{l.price}</span>
                        </div>
                        <p className="mt-2 break-all font-mono text-xs text-zinc-500">{l.url}</p>
                        <div className="mt-5 flex flex-wrap gap-3">
                            <button
                                onClick={() => copy(l.url, `${l.name} link`)}
                                data-testid={`paylink-copy-${slug}`}
                                className="flex items-center gap-2 bg-neon px-5 py-3 font-mono text-[10px] font-medium tracking-[0.2em] uppercase text-void transition-shadow duration-300 hover:shadow-[0_0_20px_rgba(0,240,255,0.4)]"
                            >
                                <Copy className="h-3.5 w-3.5" /> Copy Link
                            </button>
                            <a
                                href={`sms:?&body=${encodeURIComponent(l.message)}`}
                                data-testid={`paylink-text-${slug}`}
                                className="flex items-center gap-2 border border-neon/60 px-5 py-3 font-mono text-[10px] tracking-[0.2em] uppercase text-neon transition-[background-color,color] duration-300 hover:bg-neon hover:text-void"
                            >
                                <Smartphone className="h-3.5 w-3.5" /> Text This Link
                            </a>
                            <a
                                href={l.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid={`paylink-open-${slug}`}
                                className="flex items-center gap-2 border border-zinc-700 px-5 py-3 font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-400 transition-colors duration-300 hover:border-neon hover:text-neon"
                            >
                                <ExternalLink className="h-3.5 w-3.5" /> Open
                            </a>
                        </div>
                        <p className="mt-4 border-l-2 border-zinc-800 pl-3 text-xs italic leading-relaxed text-zinc-500">Text preview: &quot;{l.message}&quot;</p>
                    </div>
                );
            })}
        </div>
    );
}

export default function AdminDashboard() {
    const { user, logout, authHeaders } = useAuth();
    const { content, refresh } = useContentAdmin();
    const navigate = useNavigate();
    const [section, setSection] = useState("leads");
    const [draft, setDraft] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (user === null) navigate("/admin/login");
    }, [user, navigate]);

    useEffect(() => {
        const base = content || staticContent;
        setDraft(JSON.parse(JSON.stringify({ ...staticContent, ...base })));
    }, [content]);

    if (user === undefined || !draft) {
        return <div className="flex min-h-screen items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-neon" /></div>;
    }
    if (user === null) return null;

    const active = SECTIONS.find((s) => s.id === section);
    const setKey = (key, value) => setDraft((d) => ({ ...d, [key]: value }));

    const save = async () => {
        setSaving(true);
        try {
            await axios.put(`${API}/content`, draft, { headers: authHeaders() });
            await refresh();
            toast.success(`${active.label} saved — live on the site`);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    const renderEditor = () => {
        if (section === "leads") return <LeadsInbox authHeaders={authHeaders} />;
        if (section === "payments") return <PaymentsPanel draft={draft} />;
        if (section === "received") return <PaymentsReceived authHeaders={authHeaders} />;
        if (section === "settings") return <Settings authHeaders={authHeaders} />;
        if (section === "mockup") {
            const m = draft.mockupOffer || {};
            const set = (k, v) => setKey("mockupOffer", { ...m, [k]: v });
            return (
                <div className="space-y-5">
                    <label className="flex items-center gap-3 text-sm text-zinc-300">
                        <input
                            type="checkbox"
                            data-testid="mockup-enabled"
                            checked={m.enabled ?? true}
                            onChange={(e) => set("enabled", e.target.checked)}
                            className="h-4 w-4 accent-[#00F0FF]"
                        />
                        <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-500">Show this section on the homepage</span>
                    </label>
                    <Field label="Heading" value={m.heading} onChange={(v) => set("heading", v)} testid="mockup-heading" />
                    <Field label="Text" value={m.text} onChange={(v) => set("text", v)} textarea testid="mockup-text" />
                    <Field label="Button Label" value={m.ctaLabel} onChange={(v) => set("ctaLabel", v)} testid="mockup-cta" />
                </div>
            );
        }
        if (active.type === "lines") {
            return <LinesField label={active.label} value={draft[active.key]} onChange={(v) => setKey(active.key, v)} testid="editor-lines" />;
        }
        if (active.type === "options") {
            return (
                <div className="space-y-6">
                    <CsvField label="Service Options" value={draft.serviceOptions} onChange={(v) => setKey("serviceOptions", v)} testid="editor-service-options" />
                    <CsvField label="Budget Options" value={draft.budgetOptions} onChange={(v) => setKey("budgetOptions", v)} testid="editor-budget-options" />
                    <CsvField label="Timeline Options" value={draft.timelineOptions} onChange={(v) => setKey("timelineOptions", v)} testid="editor-timeline-options" />
                </div>
            );
        }
        if (section === "hero") {
            const h = draft.hero || {};
            const set = (k, v) => setKey("hero", { ...h, [k]: v });
            return (
                <div className="space-y-5">
                    <Field label="Top Tag Line" value={h.tag} onChange={(v) => set("tag", v)} testid="hero-tag" />
                    <div className="grid gap-5 sm:grid-cols-3">
                        <Field label="Headline Line 1" value={h.line1} onChange={(v) => set("line1", v)} testid="hero-line1" />
                        <Field label="Headline Line 2" value={h.line2} onChange={(v) => set("line2", v)} testid="hero-line2" />
                        <Field label="Headline Line 3 (gradient)" value={h.line3} onChange={(v) => set("line3", v)} testid="hero-line3" />
                    </div>
                    <Field label="Subtext" value={h.subtext} onChange={(v) => set("subtext", v)} textarea testid="hero-subtext" />
                    <div>
                        <p className="mb-2 font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-500">Bottom Stats</p>
                        <ListEditor
                            items={h.stats || []}
                            fields={[{ key: "value", label: "Value" }, { key: "label", label: "Label" }]}
                            onChange={(v) => set("stats", v)}
                            addLabel="Add Stat"
                            testidPrefix="hero-stats"
                        />
                    </div>
                </div>
            );
        }
        if (section === "about") {
            const a = draft.about || {};
            const set = (k, v) => setKey("about", { ...a, [k]: v });
            return (
                <div className="space-y-5">
                    <Field label="Heading" value={a.heading} onChange={(v) => set("heading", v)} testid="about-heading" />
                    <LinesField label="Paragraphs (one per line)" value={a.paragraphs} onChange={(v) => set("paragraphs", v)} testid="about-paragraphs" />
                    <CsvField label="Chips" value={a.chips} onChange={(v) => set("chips", v)} testid="about-chips" />
                    <div className="grid gap-5 sm:grid-cols-2">
                        <Field label="Photo Badge" value={a.badge} onChange={(v) => set("badge", v)} testid="about-badge" />
                        <Field label="Sign-off" value={a.signoff} onChange={(v) => set("signoff", v)} testid="about-signoff" />
                    </div>
                </div>
            );
        }
        if (active.type === "list") {
            return (
                <ListEditor
                    items={draft[active.key]}
                    fields={active.fields}
                    onChange={(v) => setKey(active.key, v)}
                    addLabel={`Add ${active.label.replace(/s$/, "")}`}
                    testidPrefix={`editor-${active.id}`}
                />
            );
        }
        if (active.type === "fields" || active.type === "fields-lines") {
            const obj = draft[active.key] || {};
            return (
                <div className="grid gap-5 sm:grid-cols-2">
                    {active.fields.map((f) => (
                        <div key={f.key} className={f.type ? "sm:col-span-2" : ""}>
                            {f.type === "lines" ? (
                                <LinesField label={f.label} value={obj[f.key]} onChange={(v) => setKey(active.key, { ...obj, [f.key]: v })} testid={`${active.id}-${f.key}`} />
                            ) : (
                                <Field label={f.label} value={obj[f.key]} onChange={(v) => setKey(active.key, { ...obj, [f.key]: v })} textarea={f.type === "textarea"} testid={`${active.id}-${f.key}`} />
                            )}
                        </div>
                    ))}
                </div>
            );
        }
        return null;
    };

    const isContentSection = !["leads", "settings", "payments", "received"].includes(section);

    return (
        <div data-testid="admin-dashboard" className="mx-auto max-w-7xl px-6 pb-24 pt-32 lg:px-10">
            <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                    <p className="font-mono text-xs tracking-[0.3em] uppercase text-neon">{"// Admin"}</p>
                    <h1 className="mt-2 font-display text-3xl font-black">Site Manager</h1>
                </div>
                <div className="flex items-center gap-3">
                    <Link to="/" data-testid="admin-view-site" className="flex items-center gap-2 border border-zinc-700 px-4 py-2.5 font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-400 transition-colors duration-300 hover:border-neon hover:text-neon">
                        <ExternalLink className="h-3.5 w-3.5" /> View Site
                    </Link>
                    <button onClick={() => { logout(); navigate("/admin/login"); }} data-testid="admin-logout" className="flex items-center gap-2 border border-zinc-700 px-4 py-2.5 font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-400 transition-colors duration-300 hover:border-red-400 hover:text-red-400">
                        <LogOut className="h-3.5 w-3.5" /> Logout
                    </button>
                </div>
            </div>

            <div className="mt-10 flex flex-col gap-10 lg:flex-row">
                <nav className="flex gap-1 overflow-x-auto lg:w-56 lg:shrink-0 lg:flex-col" aria-label="Admin sections">
                    {SECTIONS.map((s) => (
                        <button
                            key={s.id}
                            onClick={() => setSection(s.id)}
                            data-testid={`admin-tab-${s.id}`}
                            className={`whitespace-nowrap border-l-2 px-4 py-2.5 text-left font-mono text-xs tracking-[0.15em] uppercase transition-colors duration-300 ${
                                section === s.id ? "border-neon bg-surface text-neon" : "border-transparent text-zinc-500 hover:text-white"
                            }`}
                        >
                            {s.id === "leads" ? <span className="inline-flex items-center gap-2"><Inbox className="h-3.5 w-3.5" />{s.label}</span> : s.label}
                        </button>
                    ))}
                </nav>

                <div className="min-w-0 flex-1">
                    <div className="mb-6 flex items-center justify-between gap-4">
                        <h2 className="font-display text-xl font-bold">{active.label}</h2>
                        {isContentSection && (
                            <button onClick={save} disabled={saving} data-testid="admin-save-section" className="flex items-center gap-2 bg-neon px-5 py-2.5 font-mono text-xs font-medium tracking-[0.2em] uppercase text-void transition-shadow duration-300 hover:shadow-[0_0_20px_rgba(0,240,255,0.4)] disabled:opacity-60">
                                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4" /> Save</>}
                            </button>
                        )}
                    </div>
                    {renderEditor()}
                </div>
            </div>
        </div>
    );
}
